import { useEffect, useState } from "react";
import { CalendarClock, CheckCircle2, Facebook, Instagram, Loader2, Pencil, Plus, Send, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import ImageUpload from "../../components/ImageUpload";

const blank = { title: "", excerpt: "", body: "", cover_image: "", publish_date: "", publish_time: "" };

const statusStyles = {
  draft: "bg-white/10 text-white/60 border-white/20",
  scheduled: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  published: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "");
const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const splitLocal = (iso) => {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

const XIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export default function BlogTab() {
  const [posts, setPosts] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [social, setSocial] = useState({ facebook: { connected: false }, x: { connected: false } });
  const [targets, setTargets] = useState([]);

  const load = () => {
    api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  };
  useEffect(load, []);

  useEffect(() => {
    api.get("/social/status").then((r) => setSocial(r.data)).catch(() => setSocial({ facebook: { connected: false }, x: { connected: false } }));
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(blank);
    setTargets([]);
    setDialogOpen(true);
  };

  const openEdit = (post) => {
    setEditing(post);
    const when = splitLocal(post.publish_at);
    setForm({ ...blank, ...post, publish_date: when.date, publish_time: when.time, publish_at: undefined });
    setTargets([]);
    setDialogOpen(true);
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleTarget = (t) => {
    setTargets((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const save = async (status) => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (status === "scheduled" && (!form.publish_date || !form.publish_time)) {
      toast.error("Pick a date AND a time to schedule");
      return;
    }
    if (status === "published" && targets.length) {
      const connectedMap = { facebook: social.facebook?.connected, x: social.x?.connected, instagram: social.instagram?.connected };
      const names = { facebook: "Facebook", x: "X", instagram: "Instagram" };
      const missing = targets.filter((t) => !connectedMap[t]);
      if (missing.length) {
        toast.error(`Not connected yet - set up ${missing.map((t) => names[t]).join(" and ")} in the Social Media tab first`);
        return;
      }
    }
    setBusy(true);
    const payload = {
      title: form.title.trim(),
      excerpt: form.excerpt,
      body: form.body,
      cover_image: form.cover_image,
      status,
      publish_at: status === "scheduled" && form.publish_date && form.publish_time ? new Date(`${form.publish_date}T${form.publish_time}`).toISOString() : "",
      social_targets: targets,
    };
    try {
      if (editing) {
        await api.put(`/blog/${editing.id}`, payload);
      } else {
        await api.post("/blog", payload);
      }
      toast.success(status === "published" ? (targets.length ? "Post is live - sending it to your social media now" : "Post is live") : "Post scheduled");
      setDialogOpen(false);
      load();
      if (status === "published" && targets.length) {
        // Social posting runs in the background - refresh to show the results.
        setTimeout(load, 6000);
      }
    } catch (e) {
      toast.error("Could not save post", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (post) => {
    try {
      await api.delete(`/blog/${post.id}`);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success("Post deleted");
    } catch (e) {
      toast.error("Could not delete", { description: formatApiError(e) });
    }
  };

  if (posts === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="blog-admin-loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="blog-admin-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-white/50">
          {posts.length} post{posts.length === 1 ? "" : "s"} - drafts, scheduled and live
        </p>
        <Button
          onClick={openNew}
          data-testid="admin-add-post-button"
          className="bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold"
        >
          <Plus className="h-4 w-4" /> Write a Post
        </Button>
      </div>

      {posts.length === 0 ? (
        <div className="rounded-lg border border-brand-line bg-brand-surface p-16 text-center text-white/50" data-testid="blog-admin-empty">
          No blog posts yet. Write your first post and share it to Facebook.
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <div
              key={post.id}
              className="flex flex-wrap items-center gap-4 rounded-lg border border-brand-line bg-brand-surface p-4"
              data-testid={`admin-post-card-${post.slug}`}
            >
              <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-brand-obsidian">
                {post.cover_image ? (
                  <img src={post.cover_image} alt={post.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full flex items-center justify-center text-white/30 text-[10px]">No image</div>
                )}
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-display font-bold text-white">{post.title}</p>
                  <span className={`text-[9px] font-eyebrow uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-full border ${statusStyles[post.status] || statusStyles.draft}`} data-testid={`admin-post-status-${post.slug}`}>
                    {post.status === "scheduled" ? `Scheduled ${fmtDateTime(post.publish_at)}` : post.status}
                  </span>
                </div>
                <p className="text-xs text-white/50 mt-0.5">
                  /blog/{post.slug} {post.published_at ? `- live ${fmtDate(post.published_at)}` : ""}
                </p>
                {(() => {
                  const results = post.social_results || [];
                  const latest = {};
                  results.forEach((r) => { latest[r.channel] = r; });
                  const entries = Object.values(latest);
                  if (!entries.length) return null;
                  return (
                    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`social-results-${post.slug}`}>
                      {entries.map((r) => (
                        <span
                          key={r.channel}
                          title={r.ok ? r.url || "Posted" : r.error}
                          className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${r.ok ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-red-500/15 text-red-400 border-red-500/30"}`}
                          data-testid={`social-result-${r.channel}-${post.slug}`}
                        >
                          {r.ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                          {r.channel === "facebook" ? "Facebook" : "X"} {r.ok ? "posted" : "failed"}
                        </span>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => openEdit(post)}
                  data-testid={`admin-post-edit-${post.slug}`}
                  className="inline-flex items-center justify-center gap-1.5 text-[11px] font-eyebrow uppercase tracking-[0.15em] font-bold py-2 px-4 rounded-md border border-brand-line text-white/70 hover:text-white hover:border-brand-crimson/60 transition-colors"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={() => remove(post)}
                  data-testid={`admin-post-delete-${post.slug}`}
                  className="p-2 rounded-md border border-brand-line text-white/40 hover:text-red-400 hover:border-red-500/40 transition-colors"
                  aria-label={`Delete ${post.title}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-brand-surface border-brand-line max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="blog-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{editing ? "Edit Post" : "Write a Post"}</DialogTitle>
            <DialogDescription className="text-white/50 text-sm">
              Add a cover photo, title and text. Post Now publishes immediately; Schedule goes live automatically at your chosen time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <ImageUpload value={form.cover_image} onChange={(url) => setField("cover_image", url)} testid="blog-cover-upload" />
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Title *</Label>
              <Input data-testid="blog-form-title" value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Team deployed at Manchester arena event" className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Short summary (shown on the blog list and when shared)</Label>
              <Textarea data-testid="blog-form-excerpt" value={form.excerpt} onChange={(e) => setField("excerpt", e.target.value)} rows={2} placeholder="One or two lines about this post..." className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Post text</Label>
              <Textarea data-testid="blog-form-body" value={form.body} onChange={(e) => setField("body", e.target.value)} rows={10} placeholder={"Write the post here...\n\nStart a line with ## for a heading and - for bullet points."} className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-2 rounded-md border border-brand-line bg-brand-obsidian p-4">
              <Label className="text-white/70 text-xs flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-brand-crimson" /> Schedule for later (optional)
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-white/50 text-[11px]">Date</Label>
                  <Input data-testid="blog-form-schedule-date" type="date" value={form.publish_date} onChange={(e) => setField("publish_date", e.target.value)} className="bg-brand-surface border-brand-line text-white" />
                </div>
                <div className="space-y-1">
                  <Label className="text-white/50 text-[11px]">Time</Label>
                  <Input data-testid="blog-form-schedule-time" type="time" value={form.publish_time} onChange={(e) => setField("publish_time", e.target.value)} className="bg-brand-surface border-brand-line text-white" />
                </div>
              </div>
              <p className="text-[11px] text-white/40">Pick a date AND a time, then press "Schedule Post". It goes live automatically at that moment.</p>
            </div>
            <div className="space-y-2.5 rounded-md border border-brand-line bg-brand-obsidian p-4" data-testid="social-select-section">
              <Label className="text-white/70 text-xs">Select social media - these get the post at the same time (title, image and a link back to your website)</Label>
              <div className="flex flex-wrap gap-2.5">
                <button
                  type="button"
                  onClick={() => toggleTarget("facebook")}
                  disabled={!social.facebook?.connected}
                  data-testid="social-target-facebook"
                  className={`inline-flex items-center gap-2 rounded-md border px-4 py-2.5 text-[11px] font-eyebrow uppercase tracking-[0.15em] font-bold transition-all duration-300 ${
                    targets.includes("facebook")
                      ? "border-[#1877F2] bg-[#1877F2] text-white shadow-[0_0_20px_rgba(24,119,242,0.4)]"
                      : "border-brand-line bg-brand-surface text-white/70 hover:text-white"
                  } disabled:opacity-35 disabled:cursor-not-allowed`}
                >
                  <Facebook className="h-4 w-4" /> Facebook {targets.includes("facebook") ? "- will post" : ""}
                </button>
                <button
                  type="button"
                  onClick={() => toggleTarget("x")}
                  disabled={!social.x?.connected}
                  data-testid="social-target-x"
                  className={`inline-flex items-center gap-2 rounded-md border px-4 py-2.5 text-[11px] font-eyebrow uppercase tracking-[0.15em] font-bold transition-all duration-300 ${
                    targets.includes("x")
                      ? "border-white bg-white text-black"
                      : "border-brand-line bg-brand-surface text-white/70 hover:text-white"
                  } disabled:opacity-35 disabled:cursor-not-allowed`}
                >
                  <XIcon className="h-3.5 w-3.5" /> X (Twitter) {targets.includes("x") ? "- will post" : ""}
                </button>
                <button
                  type="button"
                  onClick={() => toggleTarget("instagram")}
                  disabled={!social.instagram?.connected}
                  data-testid="social-target-instagram"
                  className={`inline-flex items-center gap-2 rounded-md border px-4 py-2.5 text-[11px] font-eyebrow uppercase tracking-[0.15em] font-bold transition-all duration-300 ${
                    targets.includes("instagram")
                      ? "border-[#E1306C] bg-[#E1306C] text-white shadow-[0_0_20px_rgba(225,48,108,0.4)]"
                      : "border-brand-line bg-brand-surface text-white/70 hover:text-white"
                  } disabled:opacity-35 disabled:cursor-not-allowed`}
                >
                  <Instagram className="h-4 w-4" /> Instagram {targets.includes("instagram") ? "- will post" : ""}
                </button>
              </div>
              {(!social.facebook?.connected || !social.x?.connected || !social.instagram?.connected) && (
                <p className="text-[11px] text-white/40">Channels greyed out are not connected yet - open the "Social Media" tab (left) to connect them.</p>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-2.5">
              <Button disabled={busy} onClick={() => save("scheduled")} data-testid="blog-schedule-button" className="bg-amber-500 hover:bg-amber-400 text-black font-eyebrow uppercase tracking-[0.15em] text-[11px] font-bold">
                <CalendarClock className="h-4 w-4" /> Schedule Post
              </Button>
              <Button disabled={busy} onClick={() => save("published")} data-testid="blog-post-now-button" className="bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow uppercase tracking-[0.15em] text-[11px] font-bold">
                <Send className="h-4 w-4" /> Post Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
